import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { invalidateTaskQueries } from "@/lib/task-query-utils";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, GripVertical, CheckSquare } from "lucide-react";
import {
  DndContext,
  closestCenter,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragStartEvent,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { getBoardTypeConfig } from "@/lib/board-types";
import type { ChecklistItem } from "./task-types";

interface TaskChecklistSectionProps {
  taskId: string;
  checklistItems: ChecklistItem[];
  canEdit: boolean;
  boardType?: string;
}

function ChecklistItemContent({
  item,
  canEdit,
  onToggle,
  onDelete,
  dragHandle,
  isDragOverlay,
}: {
  item: ChecklistItem;
  canEdit: boolean;
  onToggle: (id: string, isDone: boolean) => void;
  onDelete: (id: string) => void;
  dragHandle?: React.ReactNode;
  isDragOverlay?: boolean;
}) {
  return (
    <div
      className={`flex items-center gap-2 py-1 group ${isDragOverlay ? "rounded-md border border-border bg-background shadow-md px-2" : ""}`}
      data-testid={`task-checklist-item-${item.id}`}
    >
      {dragHandle}
      <Checkbox
        checked={item.isDone}
        onCheckedChange={(checked) => onToggle(item.id, !!checked)}
        disabled={!canEdit}
        data-testid={`task-checkbox-checklist-${item.id}`}
      />
      <span className={`flex-1 text-sm ${item.isDone ? "line-through text-muted-foreground" : ""}`}>
        {item.text}
      </span>
      {canEdit && !isDragOverlay && (
        <Button
          variant="ghost"
          size="icon"
          className="invisible group-hover:visible"
          onClick={() => onDelete(item.id)}
          data-testid={`task-button-delete-checklist-${item.id}`}
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      )}
    </div>
  );
}

function SortableChecklistItem({
  item,
  canEdit,
  onToggle,
  onDelete,
}: {
  item: ChecklistItem;
  canEdit: boolean;
  onToggle: (id: string, isDone: boolean) => void;
  onDelete: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition };

  if (isDragging) {
    return (
      <div ref={setNodeRef} style={style} className="rounded-md border-2 border-dashed border-muted-foreground/30 bg-muted/30 py-3 my-0.5" />
    );
  }

  return (
    <div ref={setNodeRef} style={style}>
      <ChecklistItemContent
        item={item}
        canEdit={canEdit}
        onToggle={onToggle}
        onDelete={onDelete}
        dragHandle={
          canEdit ? (
            <span {...attributes} {...listeners} className="cursor-grab text-muted-foreground">
              <GripVertical className="w-3 h-3" />
            </span>
          ) : undefined
        }
      />
    </div>
  );
}

export function TaskChecklistSection({ taskId, checklistItems, canEdit, boardType }: TaskChecklistSectionProps) {
  const { toast } = useToast();
  const [newChecklistText, setNewChecklistText] = useState("");
  const [activeId, setActiveId] = useState<string | null>(null);
  const config = getBoardTypeConfig(boardType);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const addChecklistMutation = useMutation({
    mutationFn: async (text: string) => apiRequest("POST", `/api/tasks/${taskId}/checklist-items`, { text }),
    onSuccess: () => { invalidateTaskQueries(); setNewChecklistText(""); },
    onError: (error: Error) => toast({ title: "Error", description: error.message, variant: "destructive" }),
  });

  const toggleChecklistMutation = useMutation({
    mutationFn: async ({ itemId, isDone }: { itemId: string; isDone: boolean }) => apiRequest("PATCH", `/api/tasks/${taskId}/checklist-items/${itemId}`, { isDone }),
    onSuccess: () => invalidateTaskQueries(),
    onError: (error: Error) => toast({ title: "Error", description: error.message, variant: "destructive" }),
  });

  const deleteChecklistMutation = useMutation({
    mutationFn: async (itemId: string) => apiRequest("DELETE", `/api/tasks/${taskId}/checklist-items/${itemId}`),
    onSuccess: () => invalidateTaskQueries(),
    onError: (error: Error) => toast({ title: "Error", description: error.message, variant: "destructive" }),
  });

  const reorderChecklistMutation = useMutation({
    mutationFn: async (orderedIds: string[]) => apiRequest("POST", `/api/tasks/${taskId}/checklist-items/reorder`, { orderedIds }),
    onError: (error: Error) => {
      invalidateTaskQueries();
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  function handleDragStart(event: DragStartEvent) {
    setActiveId(event.active.id as string);
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = checklistItems.findIndex(i => i.id === active.id);
    const newIndex = checklistItems.findIndex(i => i.id === over.id);
    const reordered = arrayMove(checklistItems, oldIndex, newIndex);

    const cached = queryClient.getQueryData<{ task: any }>(["/api/tasks", taskId]);
    if (cached) {
      queryClient.setQueryData(["/api/tasks", taskId], {
        ...cached,
        task: { ...cached.task, checklistItems: reordered },
      });
    }

    reorderChecklistMutation.mutate(reordered.map(i => i.id));
  }

  const activeItem = activeId ? checklistItems.find(i => i.id === activeId) : null;

  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider flex items-center gap-1">
        <CheckSquare className="w-3 h-3" /> {config.labels.checklist}
        {checklistItems.length > 0 && (
          <Badge variant="secondary" className="text-[10px] px-1.5 py-0 ml-1" data-testid="badge-checklist-count">
            {checklistItems.filter(i => i.isDone).length}/{checklistItems.length}
          </Badge>
        )}
      </Label>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <SortableContext items={checklistItems.map(i => i.id)} strategy={verticalListSortingStrategy}>
          {checklistItems.map(item => (
            <SortableChecklistItem
              key={item.id}
              item={item}
              canEdit={canEdit}
              onToggle={(id, isDone) => toggleChecklistMutation.mutate({ itemId: id, isDone })}
              onDelete={id => deleteChecklistMutation.mutate(id)}
            />
          ))}
        </SortableContext>
        <DragOverlay>
          {activeItem && (
            <ChecklistItemContent
              item={activeItem}
              canEdit={false}
              onToggle={() => {}}
              onDelete={() => {}}
              isDragOverlay
              dragHandle={
                <span className="cursor-grabbing text-muted-foreground">
                  <GripVertical className="w-3 h-3" />
                </span>
              }
            />
          )}
        </DragOverlay>
      </DndContext>

      {canEdit && (
        <div className="flex items-center gap-2">
          <Input
            placeholder={config.labels.addItemPlaceholder}
            value={newChecklistText}
            onChange={e => setNewChecklistText(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && newChecklistText.trim()) addChecklistMutation.mutate(newChecklistText.trim()); }}
            className="h-8 text-sm"
            maxLength={1000}
            data-testid="task-input-add-checklist"
          />
          <Button
            variant="ghost"
            size="icon"
            disabled={!newChecklistText.trim() || addChecklistMutation.isPending}
            onClick={() => newChecklistText.trim() && addChecklistMutation.mutate(newChecklistText.trim())}
            data-testid="task-button-add-checklist"
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
